import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import merge from 'lodash/merge'
import { Slice, PostsCategories, BlogPageInfo } from './types'
import { success as setPost } from '../post'
import { Post, PostCategory } from '../post/types'
import { PWAThunk } from 'lib/core/data'

interface FilterPostsInput {
  searchString?: string
  sortValue?: string
  directionValue?: string
}

interface PostFilter {
  category_url_key?: string
  title?: {
    like: string
  }
}

export const initialState: Slice = {
  isFetching: false,
  isSuccess: false,
  isFailure: false,
  payload: undefined
}

const allCategories: PostCategory = {
  id: 0,
  url_key: 'all-posts',
  category_blog_image: '',
  category_blog_thumbnail: '',
  name: 'All Blog Posts',
  postSlugs: [],
  pageInfo: {}
}

const slice = createSlice({
  name: 'blog/categoryList',
  initialState,
  reducers: {
    request (state) {
      state.isFetching = true
    },

    success (state, action: PayloadAction<PostsCategories>) {
      const newItems = []
      const payloadItems = action.payload.items
      newItems.push(...payloadItems, allCategories)
      const newPayload = { items: newItems }
      state.isFetching = false
      state.isSuccess = true
      state.isFailure = false
      state.payload = merge({}, state.payload, newPayload)
    },

    failure (state) {
      state.isFetching = false
      state.isSuccess = false
      state.isFailure = true
      state.payload = initialState.payload
    },

    setPostsSlugs (state, action: PayloadAction<{ categorySlug?: string, postSlugs: string[], pageInfo: BlogPageInfo }>) {
      const { categorySlug, postSlugs, pageInfo } = action.payload

      if (!state.payload) {
        state.payload = {
          items: []
        }
      }

      const category = state.payload.items.find(({ url_key: urlKey }) => urlKey === categorySlug)
      if (category) {
        category.postSlugs = postSlugs
        category.pageInfo = pageInfo
      }

      return state
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure, setPostsSlugs } = slice.actions

export function fetchCategoryList (): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request())
    try {
      const response = await magentoClient.getClient().blog.fetchCategoryList()
      const { data } = response
      const { postCategories } = data
      if (postCategories) {
        dispatch(success(postCategories))
      } else {
        dispatch(failure())
      }
    } catch (e) {
      dispatch(failure())
    }
  }
}

export function fetchCategoryPosts (categorySlug?: string, currentPage: number = 1, filterParams?: FilterPostsInput): PWAThunk {
  const postSlugs: string[] = []
  let pageInfo: BlogPageInfo = {}
  const { searchString, sortValue: sort, directionValue: direction } = filterParams ?? {}

  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    try {
      const filter: PostFilter = {}
      if (categorySlug) filter.category_url_key = categorySlug
      if (searchString) filter.title = { like: `%${searchString}%` }

      const response = await magentoClient.getClient().blog.fetchPostList({ filters: filter, currentPage, sort, direction })

      response?.data?.posts?.items?.forEach((post: Post) => {
        postSlugs.push(post.url_key)
        dispatch(setPost({ post, slug: post.url_key }))
      })

      pageInfo = {
        ...response?.data?.posts?.page_info,
        totalCount: response?.data?.posts?.total_count
      }

      dispatch(setPostsSlugs({
        categorySlug,
        postSlugs,
        pageInfo
      }))
    } catch (e) {
      dispatch(setPostsSlugs({
        categorySlug,
        postSlugs,
        pageInfo
      }))
    }
  }
}
